import React, { createContext, useContext, useState, useCallback } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase/config";

// Create API context
const ApiContext = createContext();

// API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8889";

// System style detection - V1 or V2 based on environment
const SYSTEM_STYLE = process.env.REACT_APP_SYSTEM_STYLE || "1";

// API Provider component
export const ApiProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [interactionCount, setInteractionCount] = useState(0);
  const [error, setError] = useState(null);
  const [rateLimitError, setRateLimitError] = useState(null);

  // Create a new session
  const createSession = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/session/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setSessionId(data.sessionId);
        setInteractionCount(0);
        return data.sessionId;
      } else {
        throw new Error("Failed to create session");
      }
    } catch (err) {
      // console.error("Error creating session:", err);
      setError(err.message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  // V2 API: Unified query function with action codes
  const queryAPI = useCallback(
    async (
      action,
      userInput,
      context = [],
      forceNewSession = false,
      userProfile = null
    ) => {
      try {
        setIsLoading(true);
        setError(null);

        // Create session if not exists (force new if specified)
        const currentSessionId = forceNewSession
          ? null
          : sessionId || (await createSession());

        const response = await fetch(`${API_BASE_URL}/api/query`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            action,
            userInput,
            sessionId: currentSessionId,
            context,
            userProfile,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setSessionId(data.sessionId);
          setInteractionCount(data.interactionCount);

          // console.log("V2 Query response START:");
          // console.log(data.response);
          // console.log("V2 Query response END:");
          return {
            response: data.response,
            isReflection: data.response?.isReflection,
            isContinueJourney: data.response?.isContinueJourney,
            interactionCount: data.interactionCount,
            shouldTriggerSummary: data.shouldTriggerSummary,
            sessionId: data.sessionId,
            interactionId: data.interactionId,
          };
        } else {
          throw new Error(data.error || "Failed to get response");
        }
      } catch (err) {
        // console.error("Error in V2 query:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, createSession]
  );

  // Helper to parse rate limit error
  const parseRateLimitError = useCallback((error) => {
    // Check if it's a rate limit error
    if (
      error.code === "resource-exhausted" ||
      error.code === "functions/resource-exhausted"
    ) {
      const message = error.message || "";

      // Extract limit type (minute/hour/day)
      let limitType = "minute";
      if (message.includes("hourly")) limitType = "hour";
      else if (message.includes("daily")) limitType = "day";

      // Extract reset time from message (e.g., "in 45 seconds")
      const timeMatch = message.match(/in (\d+) (second|minute|hour)s?/);
      let resetTime = Date.now() + 60000; // Default 1 minute

      if (timeMatch) {
        const amount = parseInt(timeMatch[1]);
        const unit = timeMatch[2];

        if (unit === "second") resetTime = Date.now() + amount * 1000;
        else if (unit === "minute") resetTime = Date.now() + amount * 60 * 1000;
        else if (unit === "hour")
          resetTime = Date.now() + amount * 60 * 60 * 1000;
      }

      return {
        isRateLimit: true,
        limitType,
        resetTime,
        message,
      };
    }

    return { isRateLimit: false };
  }, []);

  // Ask Bible API - Routes to appropriate system based on SYSTEM_STYLE
  const askQuestion = useCallback(
    async (
      question,
      context = [],
      forceNewSession = false,
      userProfile = null
    ) => {
      // Route to V3 Cloud Functions
      if (SYSTEM_STYLE === "3") {
        try {
          setIsLoading(true);
          setError(null);
          setRateLimitError(null);

          const action = detectActionFromQuestion(question);
          const queryFunction = httpsCallable(functions, "queryAI");

          const result = await queryFunction({
            context,
            question,
            action,
            userProfile,
          });

          setIsLoading(false);
          setInteractionCount(context.length + 1);

          return {
            response: result.data,
            isReflection: result.data?.isReflection,
            isContinueJourney: result.data?.isContinueJourney,
            interactionCount: context.length + 1,
            shouldTriggerSummary:
              context.length + 1 >=
              (parseInt(process.env.REACT_APP_SUMMARY_THRESHOLD) || 8),
            sessionId: null, // V3 is stateless
            interactionId: null,
          };
        } catch (err) {
          setIsLoading(false);

          // Check if it's a rate limit error
          const rateLimitInfo = parseRateLimitError(err);
          if (rateLimitInfo.isRateLimit) {
            setRateLimitError(rateLimitInfo);
          } else {
            setError(err.message);
          }

          throw err;
        }
      }

      // Route to appropriate system based on SYSTEM_STYLE
      if (SYSTEM_STYLE === "2") {
        // Use V2 system with action detection
        const action = detectActionFromQuestion(question);
        return await queryAPI(
          action,
          question,
          context,
          forceNewSession,
          userProfile
        );
      }

      // Use V1 system (original behavior)
      try {
        setIsLoading(true);
        setError(null);

        // Create session if not exists (force new if specified)
        const currentSessionId = forceNewSession
          ? null
          : sessionId || (await createSession());

        const response = await fetch(`${API_BASE_URL}/api/bible/ask`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            question,
            sessionId: currentSessionId,
            context,
            userProfile,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          setSessionId(data.sessionId);
          setInteractionCount(data.interactionCount);

          // console.log("Data response START:");
          // console.log(data.response);
          // console.log("Data response END:");
          return {
            response: data.response,
            isReflection: data.response?.isReflection, // Add isReflection flag
            isContinueJourney: data.response?.isContinueJourney, // Add isContinueJourney flag
            interactionCount: data.interactionCount,
            shouldTriggerSummary: data.shouldTriggerSummary,
            sessionId: data.sessionId,
            interactionId: data.interactionId,
          };
        } else {
          throw new Error(data.error || "Failed to get response");
        }
      } catch (err) {
        // console.error("Error asking question:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, createSession, queryAPI]
  );

  // Helper function to detect action from question text (for V2 system)
  const detectActionFromQuestion = useCallback((question) => {
    const lowerQuestion = question.toLowerCase();

    // Detect continue journey
    if (lowerQuestion.startsWith("continue journey from reflection")) {
      return "continue";
    }

    // Detect reflection - only if the primary question (not context) mentions it
    // Extract the actual question without context prefix
    const questionWithoutContext = question.includes("Context:")
      ? question.split("Context:")[0].trim()
      : question;
    const lowerQuestionWithoutContext = questionWithoutContext.toLowerCase();

    if (
      lowerQuestionWithoutContext.includes("reflect and pray") ||
      lowerQuestionWithoutContext.includes("personal reflection")
    ) {
      return "reflect";
    }

    // Detect explore (has internal randomization marker)
    if (question.includes("[Internal randomization:")) {
      return "explore";
    }

    // Default to ask for regular questions
    return "ask";
  }, []);

  // Get Session Summary - Routes to appropriate system based on SYSTEM_STYLE
  const getSessionSummary = useCallback(
    async (context = []) => {
      // Route to V3 Cloud Functions
      if (SYSTEM_STYLE === "3") {
        try {
          setIsLoading(true);
          setError(null);
          setRateLimitError(null);

          const summaryFunction = httpsCallable(functions, "generateSummary");
          const result = await summaryFunction({ context });

          setIsLoading(false);

          return {
            summary: result.data,
            sessionId: null, // V3 is stateless
            interactionCount: context.length,
            sessionDuration: null,
          };
        } catch (err) {
          setIsLoading(false);

          // Check if it's a rate limit error
          const rateLimitInfo = parseRateLimitError(err);
          if (rateLimitInfo.isRateLimit) {
            setRateLimitError(rateLimitInfo);
          } else {
            setError(err.message);
          }

          throw err;
        }
      }

      // Route to appropriate system based on SYSTEM_STYLE
      if (SYSTEM_STYLE === "2") {
        // Use V2 system
        return await queryAPI("summary", "", []);
      }

      // Use V1 system (original behavior)
      try {
        setIsLoading(true);
        setError(null);

        if (!sessionId) {
          throw new Error("No active session");
        }

        const response = await fetch(`${API_BASE_URL}/api/bible/summary`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            sessionId,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          return {
            summary: data.summary,
            sessionId: data.sessionId,
            interactionCount: data.interactionCount,
            sessionDuration: data.sessionDuration,
          };
        } else {
          throw new Error(data.error || "Failed to get summary");
        }
      } catch (err) {
        // console.error("Error getting summary:", err);
        setError(err.message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [sessionId, queryAPI]
  );

  // Get session info
  const getSessionInfo = useCallback(
    async (targetSessionId = null) => {
      try {
        const currentSessionId = targetSessionId || sessionId;
        if (!currentSessionId) {
          throw new Error("No session ID provided");
        }

        const response = await fetch(
          `${API_BASE_URL}/api/session/${currentSessionId}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.success) {
          return data.session;
        } else {
          throw new Error(data.error || "Failed to get session info");
        }
      } catch (err) {
        // console.error("Error getting session info:", err);
        setError(err.message);
        throw err;
      }
    },
    [sessionId]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reset session
  const resetSession = useCallback(() => {
    setSessionId(null);
    setInteractionCount(0);
    setError(null);
  }, []);

  // Clear rate limit error
  const clearRateLimitError = useCallback(() => {
    setRateLimitError(null);
  }, []);

  const value = {
    sessionId,
    isLoading,
    interactionCount,
    error,
    rateLimitError,
    askQuestion,
    getSessionSummary,
    getSessionInfo,
    createSession,
    clearError,
    clearRateLimitError,
    resetSession,
    queryAPI, // Export V2 query function
    systemStyle: SYSTEM_STYLE, // Export system style for debugging
  };

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

// Custom hook to use API context
export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error("useApi must be used within an ApiProvider");
  }
  return context;
};

export default useApi;
